import React, { useCallback, useEffect, useRef, useState } from "react";
import { normalize } from "normalizr";
import { uniqBy, orderBy } from "lodash";
import { useDispatch } from "react-redux";
import { useService } from "student-front-commons/src/hooks";
import { mergeEntities } from "student-front-commons/src/actions/entity";
import unitSchema from "student-front-commons/src/schemas/unitSchema";
import EmptyState from "../../assets/icons/empty-state.png";
import TranslatedText from "../../core/TranslatedText";
import SubHeaderDetailsContainer from "../../core/SubHeaderDetailsContainer";
import BreadcrumbContainer from "../../core/BreadcrumbContainer";
import FlexColumn from "../../core/FlexColumn";
import Async from "../../core/Async";
import Separator from "../../core/Separator";
import HomeworkList from "./HomeworkList";
import { getHomeworks } from "student-front-commons/src/services/homeworkService";

const PAGE_SIZE = 10;

function HomeworkListScene() {
  const loaderRef = useRef(null);

  const dispatch = useDispatch();
  const [homeworks, setHomeworks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMoreHomeworks, setHasMoreHomeworks] = useState(true);

  const [isFetching, fetchHomeworks] = useService(getHomeworks, {
    autoStart: false,
    onData: (data) => {
      setLoading(false);
      if (!data || !data.docs.length || data.docs.length < PAGE_SIZE) {
        setHasMoreHomeworks(false);
      }
      const units = [];
      data.docs.forEach((homework) => {
        units.push(...homework.assigned.map((assign) => assign.unit));
      });
      const { entities } = normalize(units, [unitSchema]);
      dispatch(mergeEntities(entities));
      setHomeworks((prevState) =>
        orderBy(
          uniqBy([...prevState, ...data.docs], (homework) => homework.id),
          (homework) => homework.dueDate,
          "desc"
        )
      );
    },
    onError: () => {
      setLoading(false);
    },
  });

  const loadMoreHomeworks = useCallback(() => {
    if (isFetching || !hasMoreHomeworks) return null;
    setPage((prev) => prev + 1);
    fetchHomeworks({ page: page + 1, size: PAGE_SIZE });
  }, [page, setPage, fetchHomeworks, isFetching, hasMoreHomeworks]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching) {
          loadMoreHomeworks();
        }
      },
      { threshold: 1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [isFetching]);

  useEffect(() => {
    fetchHomeworks({ page, size: PAGE_SIZE });
  }, []);

  useEffect(() => {
    const doRefresh = () => {
      setTimeout(() => {
        fetchHomeworks({ page, size: PAGE_SIZE });
      }, 500);
    };

    window.addEventListener("new_homework", doRefresh);

    return () => {
      window.removeEventListener("new_homework", doRefresh);
    };
  }, []);

  return (
    <div>
      <SubHeaderDetailsContainer />
      <BreadcrumbContainer />
      <Separator size="xs" />
      <div className="container">
        <>
          <Async fetching={loading}>
            {homeworks.length ? (
              <>
                <HomeworkList homeworks={homeworks} />
              </>
            ) : (
              <FlexColumn
                height={350}
                marginTop={26}
                borderRadius={4}
                width="100%"
                backgroundColor="#fff"
                boxShadow="0 0 4px 0 rgba(0, 0, 0, 0.25)"
                gap={26}
                justifyContent="center"
              >
                <FlexColumn width="100%" alignItems="center">
                  <img src={EmptyState} alt="empty-state" />
                </FlexColumn>
                <FlexColumn alignItems="center" justifyContent="center" gap={5}>
                  <TranslatedText
                    translateKey="homeworks.list.emptyState.title"
                    style={{ fontWeight: 700, fontSize: 14 }}
                    as="span"
                  />
                  <TranslatedText
                    style={{ fontSize: 14 }}
                    translateKey="homeworks.list.emptyState.description"
                    as="span"
                  />
                </FlexColumn>
              </FlexColumn>
            )}
          </Async>
        </>
      </div>
      <div ref={loaderRef} style={{ height: 20 }} />
    </div>
  );
}

export default HomeworkListScene;
